// When the map stops the clock.
//
// The one clock runs behind the map (the Solar System view and the plot
// walkaround) at the pace the time control shows, but three things the map
// keeps the player from seeing must not happen out of sight (Erik's decision,
// 2026-09-26): a fight, an autopilot burn falling due, and a coasting
// ship reaching the ground. While any of them is on, the map still opens and
// the clock holds, and the map says why.
//
// Pure: no DOM, no scene, no clock. `src/main.ts` reads the sim into
// `MapHoldInput` each frame the map is open and pauses `gameClock()` on the
// answer.

export type MapHoldReason = 'fight' | 'burn' | 'impact';

export interface MapHold {
  readonly reason: MapHoldReason;
  /** The banner's line, in the player's own terms. */
  readonly line: string;
}

/**
 * Simulated seconds short of an autopilot step the map holds the clock.
 *
 * The same ten minutes the warp skip's second press stops short of a step
 * (`WARP_SKIP_NEAR_LEAD_S` in src/cruise/drive.ts): the mark past which the
 * burn is meant to be something the player watches come. Closing the map
 * there leaves ten real seconds at 1 MIN/S to see it, as the skip does.
 */
export const MAP_HOLD_BURN_LEAD_S = 600;

/**
 * Simulated seconds from the ground at which a coasting ship holds the clock.
 *
 * One hour. Near a body the warp ladder is already capped at 1 HR/S on a
 * closing approach (`WARP_APPROACH_CEILING_IDX` in src/sim/warp.ts), so an
 * hour to impact is one real second at the fastest the ladder allows there:
 * shorter than this and a player who closes the map at that ceiling has no
 * time at all to see the ground coming. At real time it is an hour to act.
 * Short enough that a ship on a slow descent from a high orbit (hours out)
 * does not lock the map the moment it starts down.
 */
export const MAP_HOLD_IMPACT_S = 3600;

export const MAP_HOLD_FIGHT_LINE = 'CLOCK HELD: A FIGHT IS ON';
export const MAP_HOLD_BURN_LINE = 'CLOCK HELD: AN AUTOPILOT BURN IS DUE';
export const mapHoldImpactLine = (bodyName: string): string =>
  `CLOCK HELD: FALLING TOWARD ${bodyName.toUpperCase()}`;

export interface MapHoldInput {
  /** A fight is mounted. */
  readonly fightOn: boolean;
  /** Simulated seconds from the clock to the autopilot's next step, 0 while a
   *  burn is in hand; null with no autopilot flying. */
  readonly burnDueInS: number | null;
  /** Simulated seconds until a coasting ship meets the ground, Infinity where
   *  it does not inside the horizon it was read over; null where the ship is
   *  not coasting (landed, parked, or no body pulling). */
  readonly impactInS: number | null;
  /** Name of the body `impactInS` is about, for the banner. */
  readonly impactBody: string;
  /** Simulated seconds this frame would move the clock. A threshold is read
   *  this far ahead, so a frame at a day a second cannot step clean over it. */
  readonly stepS: number;
}

/** Why the map holds the clock this frame, or null where it may run. */
export function mapClockHold(input: MapHoldInput): MapHold | null {
  if (input.fightOn) return { reason: 'fight', line: MAP_HOLD_FIGHT_LINE };
  const ahead = Math.max(0, input.stepS);
  if (input.burnDueInS !== null && input.burnDueInS <= MAP_HOLD_BURN_LEAD_S + ahead) {
    return { reason: 'burn', line: MAP_HOLD_BURN_LINE };
  }
  if (input.impactInS !== null && input.impactInS <= MAP_HOLD_IMPACT_S + ahead) {
    return { reason: 'impact', line: mapHoldImpactLine(input.impactBody) };
  }
  return null;
}

export interface ImpactVec {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Simulated seconds until a ship at `rel` moving at `relVel` (both relative to
 * a body of `gm`) falls to `radiusM`, coasting; Infinity where it does not
 * within `horizonS`.
 *
 * In closed form off the two-body conic: r(nu) = p / (1 + e cos nu) gives the
 * anomaly the ship meets `radiusM` at, and Kepler's equation (Barker's on a
 * parabola) the time to it. A conic whose periapsis clears `radiusM` never
 * lands, however fast it is closing. A purely radial fall, where the conic
 * degenerates to a line and p is 0, takes the radial form of the same
 * equations instead.
 */
export function timeToImpactS(
  rel: ImpactVec, relVel: ImpactVec, gm: number, radiusM: number, horizonS: number,
): number {
  const r0 = Math.hypot(rel.x, rel.y, rel.z);
  if (!(r0 > radiusM)) return 0;
  const within = (t: number): number => (t <= horizonS ? Math.max(0, t) : Number.POSITIVE_INFINITY);
  const rv = rel.x * relVel.x + rel.y * relVel.y + rel.z * relVel.z;
  const v2 = relVel.x * relVel.x + relVel.y * relVel.y + relVel.z * relVel.z;
  if (!(gm > 0)) {
    // No pull: the straight line against the sphere.
    if (!(rv < 0) || !(v2 > 0)) return Number.POSITIVE_INFINITY;
    const disc = rv * rv - v2 * (r0 * r0 - radiusM * radiusM);
    return disc < 0 ? Number.POSITIVE_INFINITY : within((-rv - Math.sqrt(disc)) / v2);
  }
  const hx = rel.y * relVel.z - rel.z * relVel.y;
  const hy = rel.z * relVel.x - rel.x * relVel.z;
  const hz = rel.x * relVel.y - rel.y * relVel.x;
  const p = (hx * hx + hy * hy + hz * hz) / gm;
  if (p < 1e-9 * r0) return within(radialFallS(r0, rv / r0, gm, radiusM));
  const c = v2 - gm / r0;
  const e = Math.hypot(
    (c * rel.x - rv * relVel.x) / gm, (c * rel.y - rv * relVel.y) / gm, (c * rel.z - rv * relVel.z) / gm);
  if (p / (1 + e) >= radiusM) return Number.POSITIVE_INFINITY;
  const PARABOLIC = 1e-9;
  if (e < 1 - PARABOLIC) {
    const a = p / (1 - e * e);
    const n = Math.sqrt(gm / (a * a * a));
    const fromPeri = (r: number): number => {
      const E = Math.acos(Math.max(-1, Math.min(1, (1 - r / a) / e)));
      return (E - e * Math.sin(E)) / n;
    };
    // Inbound, periapsis ahead: out of r0 down to the ground. Outbound: up to
    // apoapsis and back down, the rest of the orbit.
    const t = rv <= 0 ? fromPeri(r0) - fromPeri(radiusM) : (2 * Math.PI) / n - fromPeri(r0) - fromPeri(radiusM);
    return within(t);
  }
  // Open conics never come back: outbound they are gone.
  if (rv > 0) return Number.POSITIVE_INFINITY;
  if (e > 1 + PARABOLIC) {
    const a = p / (e * e - 1);
    const k = Math.sqrt((a * a * a) / gm);
    const fromPeri = (r: number): number => {
      const F = Math.acosh(Math.max(1, (1 + r / a) / e));
      return k * (e * Math.sinh(F) - F);
    };
    return within(fromPeri(r0) - fromPeri(radiusM));
  }
  const fromPeri = (r: number): number => {
    const d = Math.tan(Math.acos(Math.max(-1, Math.min(1, p / r - 1))) / 2);
    return 0.5 * Math.sqrt((p * p * p) / gm) * (d + (d * d * d) / 3);
  };
  return within(fromPeri(r0) - fromPeri(radiusM));
}

/**
 * A straight-down (or straight-up and back) fall under one body, seconds to
 * `radiusM`: the radial Kepler equation, timed out of the centre. Bound, r =
 * a(1 - cos E) and t = sqrt(a^3/gm)(E - sin E); open, r = a(cosh H - 1) and t =
 * sqrt(a^3/gm)(sinh H - H); at exactly escape speed, t = sqrt(2 r^3 / 9 gm).
 */
function radialFallS(r0: number, vr0: number, gm: number, radiusM: number): number {
  const energy = (vr0 * vr0) / 2 - gm / r0;
  if (Math.abs(energy) < 1e-12 * (gm / r0)) {
    if (vr0 > 0) return Number.POSITIVE_INFINITY;
    const fromCentre = (r: number): number => Math.sqrt((2 * r * r * r) / (9 * gm));
    return fromCentre(r0) - fromCentre(radiusM);
  }
  if (energy > 0) {
    // Open: outbound it never comes back.
    if (vr0 > 0) return Number.POSITIVE_INFINITY;
    const a = gm / (2 * energy);
    const k = Math.sqrt((a * a * a) / gm);
    const fromCentre = (r: number): number => {
      const H = Math.acosh(1 + r / a);
      return k * (Math.sinh(H) - H);
    };
    return fromCentre(r0) - fromCentre(radiusM);
  }
  const a = gm / (-2 * energy);
  const k = Math.sqrt((a * a * a) / gm);
  const fromCentre = (r: number): number => {
    const E = Math.acos(Math.max(-1, Math.min(1, 1 - r / a)));
    return k * (E - Math.sin(E));
  };
  // Inbound: straight down. Outbound: up to the apex at E = pi, then down.
  return vr0 <= 0
    ? fromCentre(r0) - fromCentre(radiusM)
    : 2 * Math.PI * k - fromCentre(r0) - fromCentre(radiusM);
}

/** A body other than the frame parent the ship might meet, relative to that parent. */
export interface ImpactSlot {
  readonly name: string;
  /** Its own GM, m3/s2: its pull on the ship, which is what lands a ship holding station over it. */
  readonly gm: number;
  /** The contact bound, metres. */
  readonly radiusM: number;
  /** Its place and velocity relative to the frame parent. */
  readonly rel: ImpactVec;
  readonly vel: ImpactVec;
}

export interface ImpactReading {
  readonly s: number;
  readonly name: string;
  /** Sweep steps the other bodies cost; 0 where every one was ruled out by reach. */
  readonly steps: number;
}

/** Most steps one secondary body's sweep may take; well past what an hour needs. */
const SECONDARY_MAX_STEPS = 20_000;
/** Longest step of that sweep, seconds: fine enough for RK4 on a low orbit. */
const SECONDARY_MAX_STEP_S = 10;

/**
 * The earliest ground a coasting ship meets among its frame parent and every
 * other body with a surface, Infinity where none inside `horizonS`.
 *
 * The same set `findGroundContact` (src/sim/ground.ts) sweeps, because a run
 * at Phobos is flown in Mars's frame the whole way in and the parent's conic
 * alone never sees it. The parent is read in closed form. Each other body is
 * swept with the ship under the parent's pull and the body's own, and the
 * body coasting under the parent's alone: a moon is what the frame parent
 * stays right down to its surface, so its pull is the one that lands a ship
 * holding station over it.
 */
export function earliestImpact(
  ship: { readonly rel: ImpactVec; readonly vel: ImpactVec },
  parent: { readonly name: string; readonly gm: number; readonly radiusM: number },
  others: readonly ImpactSlot[],
  horizonS: number,
): ImpactReading {
  let best = {
    s: parent.radiusM > 0 ? timeToImpactS(ship.rel, ship.vel, parent.gm, parent.radiusM, horizonS)
      : Number.POSITIVE_INFINITY,
    name: parent.name,
  };
  let steps = 0;
  for (const o of others) {
    const h = Math.min(horizonS, best.s);
    if (!(o.radiusM > 0) || !(h >= 0)) continue;
    const gap = gapM(ship.rel, o);
    // Already at or inside its surface: met now, with nothing to sweep.
    if (gap <= 0) {
      best = { s: 0, name: o.name };
      break;
    }
    if (gap > impactReachM(ship, o, parent, h)) continue;
    const swept = secondaryImpactS(ship, o, parent.gm, h);
    steps += swept.steps;
    if (swept.s < best.s) best = { s: swept.s, name: o.name };
  }
  return { ...best, steps };
}

function gapM(shipAt: ImpactVec, body: ImpactSlot): number {
  return Math.hypot(shipAt.x - body.rel.x, shipAt.y - body.rel.y, shipAt.z - body.rel.z) - body.radiusM;
}

/**
 * The farthest the gap to `body` can close in `horizonS`, metres, taken in the
 * body's own frame, where the gap closes at most at the relative speed.
 *
 * That speed starts at `u0` and grows two ways: falling into the body's well
 * adds at most its surface escape speed, and the parent pulls the ship and the
 * body differently by at most `A`, the sum of its pull on each at the nearest
 * each can come to it inside the horizon. So `u(t) <= u0 + v_esc + A t`, and the
 * gap closes by at most `(u0 + v_esc) H + A H^2 / 2`. Neither comes nearer the
 * parent than its surface (the parent's own impact would stop the horizon
 * first), nor nearer than its start less what its speed and the most it can be
 * pulled carry it in the horizon. Sound, not tight: a body it keeps is swept.
 */
export function impactReachM(
  ship: { readonly rel: ImpactVec; readonly vel: ImpactVec },
  body: ImpactSlot,
  parent: { readonly gm: number; readonly radiusM: number },
  horizonS: number,
): number {
  if (!(parent.radiusM > 0) || !(body.radiusM > 0)) return Number.POSITIVE_INFINITY;
  const H = horizonS;
  const len = (v: ImpactVec): number => Math.hypot(v.x, v.y, v.z);
  const gmParent = Math.max(0, parent.gm);
  const gmBody = Math.max(0, body.gm);
  const surfacePull = gmParent / (parent.radiusM * parent.radiusM);
  const nearest = (at: ImpactVec, v: ImpactVec, mostPull: number): number =>
    Math.max(parent.radiusM, len(at) - len(v) * H - 0.5 * mostPull * H * H);
  const shipNearest = nearest(ship.rel, ship.vel, surfacePull + gmBody / (body.radiusM * body.radiusM));
  const bodyNearest = nearest(body.rel, body.vel, surfacePull);
  const A = gmParent / (shipNearest * shipNearest) + gmParent / (bodyNearest * bodyNearest);
  const u0 = Math.hypot(ship.vel.x - body.vel.x, ship.vel.y - body.vel.y, ship.vel.z - body.vel.z);
  const escape = Math.sqrt((2 * gmBody) / body.radiusM);
  return (u0 + escape) * H + 0.5 * A * H * H;
}

function secondaryImpactS(
  ship: { readonly rel: ImpactVec; readonly vel: ImpactVec }, body: ImpactSlot, gm: number, horizonS: number,
): { s: number; steps: number } {
  // Ship then body: [x, y, z, vx, vy, vz] each, stepped together.
  const st = [
    ship.rel.x, ship.rel.y, ship.rel.z, ship.vel.x, ship.vel.y, ship.vel.z,
    body.rel.x, body.rel.y, body.rel.z, body.vel.x, body.vel.y, body.vel.z,
  ];
  const gap = (): number => Math.hypot(st[0]! - st[6]!, st[1]! - st[7]!, st[2]! - st[8]!) - body.radiusM;
  const closing = (): number => Math.hypot(st[3]! - st[9]!, st[4]! - st[10]!, st[5]! - st[11]!);
  let d = gap();
  if (!(d > 0)) return { s: 0, steps: 0 };
  let t = 0;
  let i = 0;
  for (; i < SECONDARY_MAX_STEPS && t <= horizonS; i++) {
    // Half the gap at the closing speed: the gap cannot be stepped clean over.
    const dt = Math.min(SECONDARY_MAX_STEP_S, Math.max(0.01, (0.5 * d) / Math.max(1, closing())));
    rk4Pair(st, gm, body.gm, dt);
    t += dt;
    d = gap();
    if (d <= 0) return { s: t <= horizonS ? t : Number.POSITIVE_INFINITY, steps: i + 1 };
  }
  return { s: Number.POSITIVE_INFINITY, steps: i };
}

const _k = [0, 1, 2, 3].map(() => new Array<number>(12).fill(0));
const _y = new Array<number>(12).fill(0);

/** Ship and body, their rates of change: the parent pulls both, the body pulls the ship. */
function pairRates(y: readonly number[], gm: number, gmBody: number, out: number[]): void {
  const pull = (x: number, yy: number, z: number, g: number): number => {
    const r2 = x * x + yy * yy + z * z;
    return g > 0 && r2 > 0 ? -g / (r2 * Math.sqrt(r2)) : 0;
  };
  const ks = pull(y[0]!, y[1]!, y[2]!, gm);
  const dx = y[0]! - y[6]!;
  const dy = y[1]! - y[7]!;
  const dz = y[2]! - y[8]!;
  const kb = pull(dx, dy, dz, gmBody);
  const kp = pull(y[6]!, y[7]!, y[8]!, gm);
  out[0] = y[3]!; out[1] = y[4]!; out[2] = y[5]!;
  out[3] = ks * y[0]! + kb * dx; out[4] = ks * y[1]! + kb * dy; out[5] = ks * y[2]! + kb * dz;
  out[6] = y[9]!; out[7] = y[10]!; out[8] = y[11]!;
  out[9] = kp * y[6]!; out[10] = kp * y[7]!; out[11] = kp * y[8]!;
}

/** One RK4 step of the ship-and-body state, in place. */
function rk4Pair(st: number[], gm: number, gmBody: number, h: number): void {
  const [k1, k2, k3, k4] = _k as [number[], number[], number[], number[]];
  pairRates(st, gm, gmBody, k1);
  for (let j = 0; j < 12; j++) _y[j] = st[j]! + (h / 2) * k1[j]!;
  pairRates(_y, gm, gmBody, k2);
  for (let j = 0; j < 12; j++) _y[j] = st[j]! + (h / 2) * k2[j]!;
  pairRates(_y, gm, gmBody, k3);
  for (let j = 0; j < 12; j++) _y[j] = st[j]! + h * k3[j]!;
  pairRates(_y, gm, gmBody, k4);
  for (let j = 0; j < 12; j++) st[j] = st[j]! + (h / 6) * (k1[j]! + 2 * k2[j]! + 2 * k3[j]! + k4[j]!);
}
